import {
	DefaultStylePanel,
	DefaultStylePanelContent,
	type Editor,
	FONT_SIZES,
	STROKE_SIZES,
	type TLUiStylePanelProps,
	Tldraw,
	loadSnapshot,
	toRichText,
	useEditor,
	useRelevantStyles,
} from "tldraw";
import { v4 as uuidv4 } from "uuid";
import "tldraw/tldraw.css";
import caseEvidenceService from "@/features/case-evaluation/api/services/evidence-service";
import CaseEvidenceEditModal from "@/features/case-evaluation/components/case-evidence-edit-modal";
import type { CaseEvidence } from "@/features/case-evaluation/types/evidence";
import type { Media } from "@/features/dashboard/types/media";
import { useEffect, useRef, useState } from "react";
import { Button } from "../core/buttons/button";
import { useModal } from "../core/modals/use-modal";

function CustomStylePanel({
	props,
	evidenceData,
	onImgUpdate,
	setIsCancelled,
	handleEvidence,
	isFullScreen,
	isLoading,
	newExhibit,
}: {
	props: TLUiStylePanelProps;
	evidenceData: CaseEvidence;
	onImgUpdate: (img: Media) => void;
	setIsCancelled: (values: boolean | undefined) => void;
	handleEvidence: (values: CaseEvidence) => void;
	isFullScreen?: boolean;
	isLoading: boolean;
	newExhibit: CaseEvidence;
}) {
	const styles = useRelevantStyles();
	const editor = useEditor();
	const modal = useModal();

	const shapeIds = editor.getCurrentPageShapeIds();

	const handleSaveDrawing = async () => {
		const { document, session } = editor.getSnapshot();
		await caseEvidenceService.saveDrawing(evidenceData.id, Object.assign({ document, session }));
	};

	const handleSaveImg = async () => {
		if (shapeIds.size === 0) return alert("No shapes on the canvas");
		const { blob } = await editor.toImage([...shapeIds], { format: "png", background: false });
		const link = document.createElement("a");
		link.href = URL.createObjectURL(blob);
		onImgUpdate?.(blob);
	};

	const handleSave = (value: boolean) => {
		if (value === true) {
			handleSaveDrawing();
			handleSaveImg();
		}
	};

	const handleAddSubmarkingOnDrawing = (submarking?: CaseEvidence) => {
		modal.openModal({
			title: "Add Submarking",
			description: `Add Submarking Evidence of ${submarking?.exhibit}`,
			component: <CaseEvidenceEditModal data={undefined} newSubmarking={submarking} onSubmit={handleEvidence} isDrawing={true} handleSave={handleSave} />,
		});
	};

	return (
		<>
			<DefaultStylePanel {...props}>
				<DefaultStylePanelContent styles={styles} />
				<div className="p-2">
					<Button className="w-full gap-2 p-1 mt-1" variant="default" type="button" size="sm" onClick={() => handleAddSubmarkingOnDrawing(evidenceData)}>
						Add Submarking
					</Button>
					<Button
						className="w-full gap-2 p-1 mt-1"
						variant="green"
						type="button"
						size="sm"
						disabled={!newExhibit}
						loading={isLoading}
						onClick={() => {
							handleSave(true);
						}}
					>
						Save
					</Button>
					{!isFullScreen && (
						<Button
							className="w-full gap-2 p-1 mt-1"
							variant="danger"
							type="button"
							size="sm"
							onClick={() => {
								modal.openModal({
									type: "confirm",
									title: "Confirm Action",
									variant: "red",
									description: "Are you sure you want to exit draw mode?",
									onConfirm: async () => {
										setIsCancelled(true);
									},
								});
							}}
						>
							Exit Draw Mode
						</Button>
					)}
				</div>
			</DefaultStylePanel>
		</>
	);
}

function getShapesWithText(editor: Editor, text: string) {
	if (!text || text.length === 0) return [];
	const shapes = editor.getCurrentPageShapes();
	const result: any[] = [];
	shapes.map((shape) => {
		const util = editor.getShapeUtil(shape);
		const shapeText = util.getText(shape);
		if (shapeText?.includes(text)) {
			result.push({ text: shapeText, shape });
		}
	});
	return result.sort((a, b) => a.text.localeCompare(b.text));
}

export default function CustomTldraw({
	data,
	onImgUpdate,
	setIsCancelled,
	newExhibit,
	handleEvidence,
	isFullScreen,
	isLoading,
}: {
	data: CaseEvidence;
	onImgUpdate: (img: Media) => void;
	setIsCancelled?: (value: boolean) => void;
	newExhibit?: string;
	handleEvidence: (value: CaseEvidence) => void;
	isFullScreen?: boolean;
	isLoading: boolean;
}) {
	const [newImg, setNewImg] = useState<Media>();

	const editorRef = useRef<Editor | null>(null);
	const [renderEditor, setRenderEditor] = useState<Editor | null>(null);
	const editor = editorRef.current;
	if (renderEditor !== editor) {
		setRenderEditor(editor);
	}
	const textShapeIdRef = useRef<string | null>(null);

	useEffect(() => {
		if (!editor) return;
		// if input is empty, remove any existing text shape
		if (!newExhibit?.trim()) {
			if (textShapeIdRef.current && editor.getShape(textShapeIdRef.current)) {
				editor.deleteShapes([textShapeIdRef.current]);
			}
			return;
		}
		const id = textShapeIdRef.current ?? `shape:${uuidv4()}`;
		textShapeIdRef.current = id;
		if (newExhibit) {
			// update the existing text shape
			editor.createShapes([
				{
					id: `shape:${uuidv4()}`,
					type: "text",
					x: 200,
					y: 200,
					props: {
						richText: toRichText(newExhibit),
						color: "black",
						font: "draw",
						size: "m",
						autoSize: true,
					},
				},
			]);
			editor.setCurrentTool("select");
		}
		const results = getShapesWithText(editor, newExhibit);
		console.log(results, "results");
	}, [newExhibit]);

	//pass image if the save button is clicked
	useEffect(() => {
		console.log(newImg, "newImg");
		if (newImg) onImgUpdate?.(newImg);
	}, [newImg]);

	const [rendered, setRendered] = useState<boolean>(false);
	const [loadingState, setLoadingState] = useState<{ status: "loading" } | { status: "ready" } | { status: "error"; error: string }>({
		status: "loading",
	});

	useEffect(() => {
		let cancelled = false;

		const fetchAndLoad = async () => {
			try {
				if (!data?.id || !editor) return;

				const drawing = await caseEvidenceService.fetchEvidenceDrawings(data.id);

				if (cancelled) return;

				loadSnapshot(editor.store, {
					document: drawing.document,
				});

				editor.zoomToFit();

				setRendered(true);
				setLoadingState({ status: "ready" });
			} catch (error: any) {
				console.log(error);
				setLoadingState({ status: "error", error: error.message }); // Something went wrong
			}
		};

		if (!rendered && editor) {
			fetchAndLoad();
		}

		return () => {
			cancelled = true;
		};
	}, [editor, data?.id, rendered]);

	return (
		<>
			<Tldraw
				components={{
					MainMenu: null,
					Minimap: null,
					ActionsMenu: null,
					StylePanel: (props: React.ComponentProps<typeof CustomStylePanel>) => (
						<CustomStylePanel
							{...props}
							evidenceData={data}
							onImgUpdate={setNewImg}
							handleEvidence={handleEvidence}
							setIsCancelled={setIsCancelled}
							isFullScreen={isFullScreen}
							isLoading={isLoading}
							newExhibit={newExhibit}
						/>
					),
				}}
				options={{ maxPages: 1 }}
				autoFocus={false}
				acceptedImageMimeTypes={["image/png", "image/jpeg", "image/jpg"]}
				maxImageDimension={90}
				overrides={{
					tools: (_editor, tools) => {
						const {
							asset: _asset,
							note: _note,
							frame: _frame,
							diamond: _diamond,
							hexagon: _hexagon,
							oval: _oval,
							rhombus: _rhombus,
							"rhombus-2": _rhombus2,
							cloud: _cloud,
							star: _star,
							heart: _heart,
							triangle: _triangle,
							trapezoid: _trapezoid,
							octagon: _octagon,
							pentagon: _pentagon,
							"arrow-up": _arrowUp,
							"arrow-down": _arrowDown,
							"arrow-left": _arrowLeft,
							"arrow-right": _arrowRight,
							"check-box": _checkBox,
							"x-box": _xBox,
							embed: _embed,
							line: _line,
							highlight: _highlight,
							text: _text,
							...filteredTools
						} = tools;
						STROKE_SIZES.s = 0.5;
						STROKE_SIZES.m = 4;
						FONT_SIZES.s = 30;
						FONT_SIZES.m = 36;
						return filteredTools;
					},
				}}
				onMount={(editor) => {
					editorRef.current = editor;
					setRenderEditor(editor);
					// disable pen mode
					editor.sideEffects.registerBeforeChangeHandler("instance", (prev, next) => {
						if (next.isPenMode) {
							return prev;
						}
						return next;
					});
					editor.setCurrentTool("draw");

					editor.updateInstanceState({ isToolLocked: true });
				}}
			/>
		</>
	);
}
